import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Box, Tab, Typography, Button } from '@mui/material'
import CanvasDraw from 'react-canvas-draw'
import _ from 'lodash'

/** TRANSLATION*/
import { useTranslation } from 'react-i18next'

/** API */
import TreatmentsAPI, { SignatureAttachmentsProps } from 'api/dentists/treatment.api'
import TreatmentSummaryApi from 'api/dentists/treatment-summary.api'

/** UTILS || SLICE */
import { resetModal } from 'app/slice/modal.slice'
import { getClinicInfo, renderAge } from 'utils/app.utils'
import { ToastContainer } from 'react-toastify'
import { setTreatmentByKey } from 'app/slice/treatment.slice'

/** COMPONENT */
import ModalCustom from 'component/ModalCustom/ModalCustom'
import { notiError, notiPopup } from 'component/notifications/notifications'
import ButtonCustom from 'component/Button/ButtonCustom'
import Loading from 'component/Loading'

/** STYLE */
import * as UseStyled from 'component/Treatments/style/popupTreatmentConsentUseStyled'
import { colors } from 'constants/theme'
import PdfInvoice from 'component/Print/pdfInvoice'
import FunctionPrint from 'component/Print/print'
import axios from 'axios'
import { Config } from 'config'
import { appendInvoice, selectCurrentInvoice, setCurrentInvoice, setDefaultInvoice } from 'app/slice/invoice.slice'


type PopupTreatmentConsentProps = {
  type: 'treatment' | 'historyTreatment' | 'payment'
  patientCaseId: number
  servicePointId: number
  setIsShow: (show: boolean) => void
  handleTreatmentCurrent?: (data: any) => void
  userId?: number
}

export default function PopupTreatmentFees(props: PopupTreatmentConsentProps) {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const branchInfo = getClinicInfo()
  const currentInvoice = useSelector(selectCurrentInvoice);
  const [doctorTab, setDoctorTab] = useState(0);
  const [treatments, setTreatments]: any = useState([]);
  const [noteNextVisit, setNoteNextVisit]: any = useState([]);
  const [treatment, setTreatment]: any = useState([]);
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [clear, setClear] = useState(false);
  const [submit, setSubmit] = useState(false);
  const [printPDF, setPrintPDF] = useState(false);
  const [defaultDoctorSignature, setDefaultDoctorSignature] = useState<any>();
  const [defaultPatientSignature, setDefaultPatientSignature] = useState<any>();
  const [modalType, setModalType] = useState<'doctor' | 'patient'>('doctor');
  const [disabledSignature, setDisabledSignature] = useState(false);
  const [disabledBtn, setDisabledBtn] = useState(false);
  const [canDraw, setCanDraw] = useState(false);
  const [canvasKey, setCanvasKey] = useState(Date.now());

  const canvasDoctorRef: any = useRef()
  const canvasPatientRef: any = useRef()
  const defaultSignature: SignatureAttachmentsProps = {
    doctorId: 0,
    servicePointId: props.servicePointId,
    doctorSignature: '',
    patientSignature: ''
  }
  const [signatureCurrent, setSignatureCurrent] = useState<SignatureAttachmentsProps>(defaultSignature);
  const transformScale = 0.7

  const loadData = async (patientCaseId: number) => {
    const resp = await TreatmentsAPI.findInvoiceTreatment(patientCaseId).finally(() => setLoading(false));
    if (resp.status === 200) {
      const invoices = props.type === 'historyTreatment' ?
        _.filter(resp.data, (data) => data.invoice) :
        _.filter(resp.data, (data) => !_.isEmpty(data.summeryTreatments) || !_.isEmpty(data?.summeryProducts));
      await joinNextVisit(invoices)
      setTreatments(invoices);
      setTreatment(invoices[doctorTab]);
      setDisabledBtn(props.userId !== invoices[doctorTab]?.doctorId)
      invoices.forEach((invoice, index) => {
        if (invoice?.invoice) {
          dispatch(appendInvoice({
            index, value: {
              servicePointId: props.servicePointId,
              doctorId: invoice?.invoice?.doctorId,
              doctorSignature: invoice?.invoice?.doctorSignature,
              patientSignature: invoice?.invoice?.patientSignature
            }
          }));
        } else if (invoice.doctorSignature) {
          let invoiceData: any = {};
          convertImageToBase64(`${Config.REACT_APP_IMAGE_URL}/${invoice.doctorSignature}`)
            .then((base64) => {
              let base64string: any = `${base64}`.split(',')
              base64string = base64string[base64string.length - 1]
              invoiceData = {
                ...currentInvoice,
                doctorId: invoice?.doctorId,
                doctorSignature: base64string,
                patientSignature: invoice?.invoice?.patientSignature
              }
            })
            .catch((error) => console.error("Error:", error));
          dispatch(appendInvoice({ index, value: invoiceData }));
        } else {
          dispatch(appendInvoice({
            index, value: {
              ...currentInvoice,
              doctorId: invoice?.doctorId,
              doctorSignature: '',
              patientSignature: ''
            }
          }));
        }
      });
      if (invoices) {
        if (invoices[doctorTab]?.invoice) {
          dispatch(setCurrentInvoice({
            ...currentInvoice,
            doctorId: invoices[doctorTab]?.doctorId,
            doctorSignature: invoices[doctorTab]?.invoice.doctorSignature,
            patientSignature: invoices[doctorTab]?.invoice?.patientSignature
          }));
        } else if (invoices[doctorTab]?.doctorSignature) {
          convertImageToBase64(`${Config.REACT_APP_IMAGE_URL}/${invoices[doctorTab]?.doctorSignature}`)
            .then((base64) => {
              let base64string: any = `${base64}`.split(',')
              base64string = base64string[base64string.length - 1]
              setDefaultDoctorSignature(base64)
              dispatch(setCurrentInvoice({
                ...currentInvoice,
                doctorId: invoices[doctorTab]?.doctorId,
                doctorSignature: base64string,
                patientSignature: invoices[doctorTab]?.invoice?.patientSignature
              }));
            })
            .catch((error) => console.error("Error:", error));
        } else {
          dispatch(setCurrentInvoice({
            ...currentInvoice,
            doctorId: invoices[doctorTab]?.doctorId,
            doctorSignature: invoices[doctorTab]?.invoice?.doctorSignature,
            patientSignature: invoices[doctorTab]?.invoice?.patientSignature
          }));
        }
      }
    }
  };


  const joinNextVisit = (data: any) => {
    const dataInvoices: any = []
    data?.forEach((item: any) => {
      item.summeryTreatments.forEach((e: any) => {
        dataInvoices.push({ doctorId: e.doctorId, noteNextVisit: e.noteNextVisit })
      })
    })

    const grouped = dataInvoices.reduce((acc: any, cur: any) => {
      if (cur.noteNextVisit.trim() === "") {
        return acc;
      }
      if (!acc[cur.doctorId]) {
        acc[cur.doctorId] = [];
      }
      acc[cur.doctorId].push(cur.noteNextVisit);
      return acc;
    }, {});

    const result = Object.entries(grouped).map(([doctorId, notes]: any) => ({
      doctorId: Number(doctorId),
      noteNextVisit: notes.join(';')
    }));
    setNoteNextVisit(result)
  }

  const handleChangeTab = (event: React.SyntheticEvent, newValue: number) => {
    setDisabledBtn(props.userId !== treatments[newValue]?.doctorId)
    if (treatments[newValue]?.invoice) {
      dispatch(setCurrentInvoice({
        ...currentInvoice,
        doctorId: treatments[newValue]?.doctorId,
        doctorSignature: treatments[newValue]?.invoice.doctorSignature,
        patientSignature: treatments[newValue]?.invoice?.patientSignature
      }));
    } else {
      if (treatments[newValue].doctorSignature !== null) {
        convertImageToBase64(`${Config.REACT_APP_IMAGE_URL}/${treatments[newValue]?.doctorSignature}`)
          .then((base64) => {
            let base64string: any = `${base64}`.split(',')
            base64string = base64string[base64string.length - 1]
            setDefaultDoctorSignature(base64)
            setDefaultPatientSignature(undefined)
            dispatch(setCurrentInvoice({
              ...currentInvoice,
              doctorId: treatments[newValue]?.doctorId,
              doctorSignature: base64string,
              patientSignature: treatments[newValue]?.invoice?.patientSignature
            }));
          })
          .catch((error) => console.error("Error:", error));
      } else {
        setDefaultDoctorSignature(undefined)
        setDefaultPatientSignature(undefined)
      }
    }

    if ((currentInvoice.doctorSignature && currentInvoice.patientSignature) && (!treatment?.invoice?.doctorSignature && !treatment?.invoice?.patientSignature)) {
      return notiPopup('custom', '', (
        <div className={'d-flex align-items-center justify-content-between'}>
          <span>{t(`MODAL_TREATMENT_CONSENT.MESSAGE.INPROGRESS`)}</span>
          <Button variant="text" disableRipple onClick={() => {
            clearCanvas('both');
            notiPopup('clear');
          }}
            sx={{ color: colors.statusWarning, fontSize: 16, fontWeight: 400, padding: 0, lineHeight: 1, backgroundColor: 'transparent !important' }}>
            {t(`MODAL_TREATMENT_CONSENT.BUTTON.DISCARD`)}
          </Button>
        </div>
      ));
    }
    clearCanvas('both');
    setDoctorTab(newValue);
    setTreatment(treatments[newValue]);
    dispatch(setCurrentInvoice({
      ...currentInvoice,
      doctorId: treatments[newValue]?.doctorId,
      doctorSignature: treatments[newValue]?.invoice?.doctorSignature,
      patientSignature: treatments[newValue]?.invoice?.patientSignature
    }))
  };

  const onSave = async (type: 'save' | 'print') => {
    setDefaultDoctorSignature(`data:image/png;base64,${currentInvoice.doctorSignature}`)
    setDefaultPatientSignature(`data:image/png;base64,${currentInvoice.patientSignature}`)
    await TreatmentsAPI.saveInvoiceTreatment(props.patientCaseId, currentInvoice).then(async (e) => {
      clearCanvas('both');
      setClear(false);
      setPrintPDF(true)
      await loadData(props.patientCaseId);
      notiPopup('success', t(`MODAL_TREATMENT_CONSENT.MESSAGE.SUCCESS.SAVE`));
      await handleChangeTreatmentConsent(props.patientCaseId);
    }).catch((e) => {
      notiError(t('MODAL_TREATMENT_CONSENT.MESSAGE.ERROR.DEFAULT'));
    });
  };


  const onCancel = async () => {
    setDefaultDoctorSignature(undefined)
    setDefaultPatientSignature(undefined)
    await TreatmentsAPI.deleteInvoiceTreatment(treatment?.invoice?.patientInvoiceId).then(async (e) => {
      clearCanvas('both');
      setClear(true);
      loadData(props.patientCaseId);
      notiPopup('success', t(`MODAL_TREATMENT_CONSENT.MESSAGE.SUCCESS.CANCEL`));
      await handleChangeTreatmentConsent(props.patientCaseId);
    }).catch((e) => {
      notiError(t('MODAL_TREATMENT_CONSENT.MESSAGE.ERROR.DEFAULT'));
    });
  };

  const onClosePopup = () => {
    setPrintPDF(true)
    dispatch(resetModal())
    props.setIsShow(false)
  }

  const renderCanvas = (type: 'doctor' | 'patient') => {
    if (type === 'doctor') {
      if (canvasDoctorRef.current) {
        const base64 = canvasDoctorRef.current.canvasContainer.childNodes[1].toDataURL('image/jpg')
        let base64string: any = `${base64}`.split(',')
        base64string = base64string[base64string.length - 1]
        setSignatureCurrent({ doctorId: treatment?.doctorId, servicePointId: props.servicePointId, doctorSignature: base64string, patientSignature: currentInvoice?.patientSignature })
      }
    } else {
      if (canvasPatientRef.current) {
        const base64 = canvasPatientRef.current.canvasContainer.childNodes[1].toDataURL('image/jpg')
        let base64Doctor = currentInvoice?.doctorSignature;
        let base64string: any = `${base64}`.split(',')
        base64string = base64string[base64string.length - 1]
        if (treatment?.doctorSignature && currentInvoice?.doctorSignature === '') {
          convertImageToBase64(`${Config.REACT_APP_IMAGE_URL}/${treatment?.doctorSignature}`).then((base64) => {
            base64Doctor = `${base64}`.split(',')
            base64Doctor = base64Doctor[base64Doctor.length - 1]
          }).catch((error) => console.error("Error:", error));
        }
        setSignatureCurrent({ doctorId: treatment?.doctorId, servicePointId: props.servicePointId, doctorSignature: base64Doctor, patientSignature: base64string })
      }
    }
  }



  const clearCanvas = (type: 'doctor' | 'patient' | 'both') => {
    const clearSignature = (
      ref: React.RefObject<any>,
      defaultSignature: any,
      setDefaultSignature: (value: any) => void
    ) => {
      if (defaultSignature) {
        setDefaultSignature(undefined);
      } else if (ref.current) {
        ref.current.clear();
        ref.current.imgSrc = '';
      }
    };

    if (type === 'doctor' || type === 'both') {
      clearSignature(canvasDoctorRef, defaultDoctorSignature, setDefaultDoctorSignature);
      if (type === 'doctor') {
        setSignatureCurrent(prev => ({
          ...prev,
          doctorSignature: '',
        }));
      }
    }

    if (type === 'patient' || type === 'both') {
      clearSignature(canvasPatientRef, defaultPatientSignature, setDefaultPatientSignature);
      if (type === 'patient') {
        setSignatureCurrent(prev => ({
          ...prev,
          patientSignature: '',
        }));
      }
    }

    if (type === 'both') {
      setSignatureCurrent(prev => ({
        ...prev,
        doctorSignature: '',
        patientSignature: '',
      }));
    }

    setCanvasKey(Date.now());
  };

  const handleChange = async () => {
    if (currentInvoice?.doctorSignature) {
      setDefaultDoctorSignature(`data:image/png;base64,${currentInvoice?.doctorSignature}`)
    } else {
      convertImageToBase64(`${Config.REACT_APP_IMAGE_URL}/${treatment?.doctorSignature}`)
        .then((base64) => {
          let base64string: any = `${base64}`.split(',')
          base64string = base64string[base64string.length - 1]
          dispatch(setCurrentInvoice({
            ...currentInvoice,
            doctorId: treatment[doctorTab]?.userId,
            doctorSignature: base64string
          }))
          setDefaultDoctorSignature(base64)
        }).catch((error) => console.error("Error:", error));
    }
    if (currentInvoice?.patientSignature) {
      setDefaultPatientSignature(`data:image/png;base64,${currentInvoice?.patientSignature}`)
    }
  }


  const handleChangeTreatmentConsent = async (patientCaseId: number) => {
    const findTry = await TreatmentSummaryApi.findTreatmentSummary(patientCaseId)
    if (findTry.status === 200) {
      const summeryProducts: any = []
      if (!_.isEmpty(findTry.data.summeryProducts)) {
        _.map(findTry.data.summeryProducts, (prod, index) => {
          summeryProducts.push({ ...prod, total: ((prod.price * prod.qty) - prod.discount) + prod.additional })
        })
      }
      props?.handleTreatmentCurrent && props.handleTreatmentCurrent([...findTry.data.summeryTreatments, ...summeryProducts])
      dispatch(setTreatmentByKey({ key: 'summeryTreatments', value: [...findTry.data.summeryTreatments, ...summeryProducts] }))
      dispatch(setTreatmentByKey({ key: 'patientDiagnosis', value: [...findTry.data.patientDiagnosis] }))
      dispatch(setTreatmentByKey({ key: 'patientRecommends', value: [...findTry.data.patientRecommends] }))
    }
  }

  useEffect(() => {
    if (props.patientCaseId) loadData(props.patientCaseId)
  }, [props.patientCaseId]);

  const convertImageToBase64 = async (url: string): Promise<string> => {
    const response = await axios.get(url, { responseType: "blob" });
    const blob = response.data;
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(blob);
    });
  };


  const pdfInvoiceRef = useRef<any>(null)

  return (
    <ModalCustom
      size={'xl'}
      title={t('PDF_INVOICE.TITLE')}
      alignFooter={'end'}
      fullscreen
      footerInline
      onClose={() => onClosePopup()}
      closeButton={_.isEmpty(treatments)}
      modalStyle={'modal-med-certificate modal-treatment-consent'}
      component={
        <>
          {!loading && (
            !_.isEmpty(treatments) && (
              <>
                <Box sx={{ borderBottom: 1, borderColor: colors.extraLightBlue }}>
                  <UseStyled.DoctorTabs
                    value={doctorTab}
                    onChange={handleChangeTab}
                    aria-label="doctor-tabs"
                    variant="scrollable"
                    scrollButtons
                    allowScrollButtonsMobile>
                    {_.map(treatments, (trm, index: number) => {
                      return <Tab key={index} label={<span className='text-ellipsis w-100'>{`${trm?.firstname} ${trm?.lastname}`}</span>} {...a11yProps(index)} />
                    })}
                  </UseStyled.DoctorTabs>
                </Box>
                <ToastContainer enableMultiContainer containerId={'popup'} className={'Toast-popup'} />
                <Box sx={{ padding: '16px 0', display: 'flex', justifyContent: 'center', height: `calc(${420 * transformScale}mm + 32px)` }}>
                  <Box sx={{ transform: `scale(${transformScale})`, height: '0px' }}>
                    <Box ref={pdfInvoiceRef}>
                      <PdfInvoice
                        type={props.type}
                        docterId={doctorTab}
                        noteNextVisit={noteNextVisit}
                        treatment={treatment}
                        clear={clear}
                        submit={submit}
                        disabledBtn={disabledBtn}
                        onSubmit={(e) => {
                          if (e) { dispatch(setCurrentInvoice(signatureCurrent)) }
                        }}
                        childrenModal={
                          (modalType === 'doctor' ?
                            <Box className={`signatureWrap-box w-100`} key={'doctor'}>
                              <Box className={`${treatment?.invoice?.doctorSignature ? 'pe-none' : ''}`}>
                                <UseStyled.SignBoxFee className='mx-auto' >
                                  {treatment?.invoice?.doctorSignature && (
                                    <img src={`data:image/png;base64,${treatment?.invoice?.doctorSignature}`} className="w-100 h-100" alt="" />
                                  ) || (
                                      <Box className={`clear-box ${props.type === 'historyTreatment' ? 'd-none opacity-0' : ''}`}>
                                        <ButtonCustom variant={'outlined'} textButton={t('MODAL_TREATMENT_CONSENT.BUTTON.CLEAR_CANVAS')} onClick={() => clearCanvas(modalType)} />
                                      </Box>
                                    )}
                                  <Box className="canvas-box" >
                                    <CanvasDraw
                                      immediateLoading
                                      key={canvasKey}
                                      hideGrid
                                      hideInterface
                                      brushRadius={2}
                                      brushColor={colors.black}
                                      ref={canvasDoctorRef}
                                      imgSrc={defaultDoctorSignature}
                                      disabled={defaultDoctorSignature !== undefined}
                                      onChange={() => renderCanvas('doctor')}
                                    />
                                  </Box>
                                </UseStyled.SignBoxFee>
                              </Box>
                              <Box className='text-center mt-2'>
                                <Typography sx={{ fontWeight: 500 }}><span className='d-md-none'>(</span>{`${treatment?.firstname} ${treatment?.lastname}`}<span className='d-md-none'>)</span></Typography>
                                <Typography className='d-none d-md-block' sx={{ fontSize: 14 }}>{t('MODAL_TREATMENT_CONSENT.SIGN.DOCTOR')}</Typography>
                                <Typography className='d-none d-md-block' sx={{ fontSize: 14 }}>Dentist{"'"}s Signature</Typography>
                              </Box>
                            </Box> :
                            <Box className={`signatureWrap-box w-100`} key={'patient'}>
                              <Box className={`${treatment?.invoice?.patientSignature ? 'pe-none' : ''}`}>
                                <Box>{treatment?.invoice?.patientSignature}</Box>
                                <UseStyled.SignBoxFee className='mx-auto'>
                                  {treatment?.invoice?.patientSignature && (
                                    <img src={`data:image/png;base64,${treatment?.invoice?.patientSignature}`} className="w-100 h-100" alt="" />
                                  ) || (
                                      <Box className={`clear-box ${props.type === 'historyTreatment' ? 'd-none opacity-0' : ''}`}>
                                        <ButtonCustom variant={'outlined'} textButton={t('MODAL_TREATMENT_CONSENT.BUTTON.CLEAR_CANVAS')} onClick={() => clearCanvas('patient')} />
                                      </Box>
                                    )}
                                  <Box className="canvas-box">
                                    <CanvasDraw
                                      key={canvasKey}
                                      hideGrid
                                      hideInterface
                                      brushRadius={2}
                                      lazyRadius={0}
                                      brushColor={colors.black}
                                      ref={canvasPatientRef}
                                      imgSrc={defaultPatientSignature}
                                      disabled={defaultPatientSignature !== undefined}
                                      onChange={() => renderCanvas('patient')} />
                                  </Box>
                                </UseStyled.SignBoxFee>
                              </Box>
                              <Box className='text-center mt-2'>
                                <Typography sx={{ fontWeight: 500 }}><span className='d-md-none'>(</span>{`${treatment?.patientInfo?.firstname} ${treatment?.patientInfo?.lastname}`}<span className='d-md-none'>)</span></Typography>
                                <Typography className='d-none d-md-block' sx={{ fontSize: 14 }}>{t('MODAL_TREATMENT_CONSENT.SIGN.PATIENT')}</Typography>
                                <Typography className='d-none d-md-block' sx={{ fontSize: 14 }}>Patient{"'"}s Signature</Typography>
                              </Box>
                            </Box>
                          )
                        }
                        openModal={(e) => {
                          setModalType(e);
                          handleChange();
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
                <UseStyled.ModalFooter className='d-flex flex-column flex-md-row align-items-center justify-content-md-end'>
                  <Box className='d-flex align-items-center'>
                    <Box className='pr-1 pr-md-2'>
                      <ButtonCustom
                        className={'btn-cancel'}
                        variant={'outlined'}
                        textButton={t('BUTTON.CLOSE')}
                        onClick={onClosePopup} />
                    </Box>
                    <Box className={`pl-1 pl-md-2`}>
                      <FunctionPrint
                        content={() => pdfInvoiceRef.current}
                        docTitle={t("PDF_INVOICE.TITLE")}
                        removeIfram
                        trigger={() => (
                          <ButtonCustom
                            disabled={!currentInvoice?.doctorSignature || !currentInvoice?.patientSignature}
                            className={'btn-print'}
                            variant={'outlined'}
                            textButton={t('BUTTON.PRINT')}
                            onClick={() => setPrintPDF(true)}
                          />
                        )}
                        style={'@page { size: 297mm 420mm;  margin: 0cm;  } @media print { body { -webkit-print-color-adjust: exact; } '}
                        beforePrint={() => { onClosePopup() }}
                        afterPrint={async () => {
                          if (currentInvoice?.doctorSignature && currentInvoice?.patientSignature && !treatment?.invoice?.patientInvoiceId) {
                            await onSave('save')
                          }
                        }}
                      />
                    </Box>
                  </Box>
                  {props.type !== 'historyTreatment' && (
                    <Box className='pt-2 pt-md-0 pl-md-3'>
                      {(treatment?.invoice?.doctorSignature && treatment?.invoice?.patientSignature) && (
                        <ButtonCustom
                          disabled={
                            Config.IS_TDH === '1' && props.type === 'payment' ? false :
                              branchInfo.pdfInvoiceTreatmentCondition === '0' ? props.userId !== currentInvoice?.doctorId : false
                          }
                          className={'btn-discard'}
                          textButton={t('MODAL_TREATMENT_CONSENT.BUTTON.CANCEL_DOCUMENT')}
                          onClick={onCancel} />
                      ) || (
                          <ButtonCustom
                            disabled={!currentInvoice?.doctorSignature || !currentInvoice?.patientSignature}
                            className={'btn-save'}
                            textButton={t('BUTTON.SAVE')}
                            onClick={() => onSave('save')} />
                        )}
                    </Box>
                  )}
                </UseStyled.ModalFooter>
                {pdfLoading && <Loading show={pdfLoading} type='fullLoading' />}
              </>
            ) || (
              <Box className='d-flex align-items-center justify-content-center h-100'>
                <Typography className='text-center'>{props.type === 'historyTreatment' ? t('REGISTER.NO_ITEM') : t('MODAL_TREATMENT_CONSENT.NO_TREATMENT')}</Typography>
              </Box>
            )
          ) || (
              <Loading show={loading} type='softLoading' className='m-auto' />
            )
          }
        </>
      }
    />
  )
}

function a11yProps(index: number) {
  return {
    id: `doctor-tab-${index}`,
    'aria-controls': `doctor-tabpanel-${index}`,
  };
}